import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Package, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  adminGetProtocols, adminCreateProtocol, adminUpdateProtocol, adminDeleteProtocol,
  adminGetConditions, adminGetProducts, adminGetProtocolProducts,
  adminCreateProtocolProduct, adminDeleteProtocolProduct
} from '@/lib/api';
import { toast } from 'sonner';

const AdminProtocols = () => {
  const [protocols, setProtocols] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProtocol, setEditingProtocol] = useState(null);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [protocolProducts, setProtocolProducts] = useState([]);
  const [formData, setFormData] = useState({
    condition_id: '',
    protocol_name: '',
    consumer_title: '',
    description: '',
    display_priority: 0
  });
  const [productFormData, setProductFormData] = useState({
    product_id: '',
    role: 'primary',
    sort_order: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [protocolsData, conditionsData, productsData] = await Promise.all([
        adminGetProtocols(),
        adminGetConditions(),
        adminGetProducts()
      ]);
      setProtocols(protocolsData || []);
      setConditions(conditionsData || []);
      setProducts(productsData || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadProtocolProducts = async (protocolId) => {
    try {
      const data = await adminGetProtocolProducts(protocolId);
      setProtocolProducts(data || []);
    } catch (error) {
      toast.error('Failed to load protocol products');
    }
  };

  const openCreateDialog = () => {
    setEditingProtocol(null);
    setFormData({
      condition_id: conditions[0]?.id || '',
      protocol_name: '',
      consumer_title: '',
      description: '',
      display_priority: 0
    });
    setDialogOpen(true);
  };

  const openEditDialog = (protocol) => {
    setEditingProtocol(protocol);
    setFormData({
      condition_id: protocol.condition_id,
      protocol_name: protocol.protocol_name,
      consumer_title: protocol.consumer_title,
      description: protocol.description || '',
      display_priority: protocol.display_priority || 0
    });
    setDialogOpen(true);
  };

  const openProductDialog = (protocol) => {
    setSelectedProtocol(protocol);
    loadProtocolProducts(protocol.id);
    setProductFormData({ product_id: '', role: 'primary', sort_order: 0 });
    setProductDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProtocol) {
        await adminUpdateProtocol(editingProtocol.id, formData);
        toast.success('Protocol updated');
      } else {
        await adminCreateProtocol(formData);
        toast.success('Protocol created');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save protocol');
    }
  };

  const handleDelete = async (protocol) => {
    if (!window.confirm(`Delete "${protocol.consumer_title}"?`)) return;
    try {
      await adminDeleteProtocol(protocol.id);
      toast.success('Protocol deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete protocol');
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    if (!productFormData.product_id || !selectedProtocol) return;
    try {
      await adminCreateProtocolProduct({
        protocol_id: selectedProtocol.id,
        ...productFormData,
        sort_order: parseInt(productFormData.sort_order) || 0
      });
      toast.success('Product added to protocol');
      loadProtocolProducts(selectedProtocol.id);
      setProductFormData({ product_id: '', role: 'primary', sort_order: 0 });
    } catch (error) {
      toast.error('Failed to add product');
    }
  };

  const handleRemoveProduct = async (ppId) => {
    try {
      await adminDeleteProtocolProduct(ppId);
      toast.success('Product removed');
      loadProtocolProducts(selectedProtocol.id);
    } catch (error) {
      toast.error('Failed to remove product');
    }
  };

  const getConditionName = (conditionId) => {
    const condition = conditions.find(c => c.id === conditionId);
    return condition?.consumer_label || 'Unknown';
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'primary': return <Badge className="badge-primary">Primary</Badge>;
      case 'secondary': return <Badge className="badge-secondary">Secondary</Badge>;
      default: return <Badge className="badge-optional">Optional</Badge>;
    }
  };

  if (loading) {
    return <div className="text-stone-400 p-8">Loading protocols...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl text-[#2C2C2C] mb-1">Protocols</h1>
          <p className="text-stone-600 text-sm">Supplement protocols for conditions</p>
        </div>
        <Button onClick={openCreateDialog} className="btn-primary" data-testid="add-protocol-btn">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Add Protocol
        </Button>
      </div>

      {conditions.length === 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <p className="text-amber-800 text-sm">
              Create conditions first before adding protocols.
            </p>
          </CardContent>
        </Card>
      )}

      {protocols.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <p className="text-stone-500 mb-4">No protocols created yet</p>
            <Button onClick={openCreateDialog} variant="outline" className="btn-outline" disabled={conditions.length === 0}>
              Create Your First Protocol
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {protocols.map((protocol) => (
            <Card key={protocol.id} className="card-hover" data-testid={`protocol-card-${protocol.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg font-serif">{protocol.consumer_title}</CardTitle>
                    <p className="text-xs text-stone-500 mt-1">{protocol.protocol_name}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openProductDialog(protocol)}
                      className="h-8 w-8 text-stone-400 hover:text-[#4A6763]"
                      title="Manage Products"
                    >
                      <Package className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(protocol)}
                      className="h-8 w-8 text-stone-400 hover:text-[#4A6763]"
                    >
                      <Pencil className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(protocol)}
                      className="h-8 w-8 text-stone-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <Badge variant="outline" className="mb-3">
                  {getConditionName(protocol.condition_id)}
                </Badge>
                {protocol.description && (
                  <p className="text-sm text-stone-600 line-clamp-2">{protocol.description}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Protocol Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingProtocol ? 'Edit Protocol' : 'Add Protocol'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <label className="form-label">Condition</label>
                <Select
                  value={formData.condition_id}
                  onValueChange={(value) => setFormData({ ...formData, condition_id: value })}
                >
                  <SelectTrigger data-testid="protocol-condition-select">
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent>
                    {conditions.map((condition) => (
                      <SelectItem key={condition.id} value={condition.id}>
                        {condition.consumer_label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="form-label">Internal Name</label>
                <Input
                  value={formData.protocol_name}
                  onChange={(e) => setFormData({ ...formData, protocol_name: e.target.value })}
                  placeholder="e.g., digestive_support_basic"
                  required
                  data-testid="protocol-name-input"
                />
              </div>
              <div>
                <label className="form-label">Consumer Title</label>
                <Input
                  value={formData.consumer_title}
                  onChange={(e) => setFormData({ ...formData, consumer_title: e.target.value })}
                  placeholder="e.g., Digestive Support Protocol"
                  required
                  data-testid="protocol-title-input"
                />
              </div>
              <div>
                <label className="form-label">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description..."
                  rows={3}
                  data-testid="protocol-description-input"
                />
              </div>
              <div>
                <label className="form-label">Display Priority</label>
                <Input
                  type="number"
                  value={formData.display_priority}
                  onChange={(e) => setFormData({ ...formData, display_priority: parseInt(e.target.value) || 0 })}
                  data-testid="protocol-priority-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-protocol-btn">
                {editingProtocol ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage Protocol Products Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">
              Products in {selectedProtocol?.consumer_title}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {/* Add Product Form */}
            <form onSubmit={handleAddProduct} className="flex flex-col sm:flex-row gap-3 mb-6">
              <Select
                value={productFormData.product_id}
                onValueChange={(value) => setProductFormData({ ...productFormData, product_id: value })}
              >
                <SelectTrigger className="flex-1" data-testid="add-protocol-product-select">
                  <SelectValue placeholder="Select product to add" />
                </SelectTrigger>
                <SelectContent>
                  {products.filter(p => !protocolProducts.find(pp => pp.product_id === p.id)).map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.product_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={productFormData.role}
                onValueChange={(value) => setProductFormData({ ...productFormData, role: value })}
              >
                <SelectTrigger className="w-32" data-testid="add-protocol-product-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">Primary</SelectItem>
                  <SelectItem value="secondary">Secondary</SelectItem>
                  <SelectItem value="optional">Optional</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" className="btn-primary" disabled={!productFormData.product_id} data-testid="add-protocol-product-btn">
                <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} />
                Add
              </Button>
            </form>

            {/* Product List */}
            {protocolProducts.length === 0 ? (
              <p className="text-stone-500 text-center py-8">No products assigned to this protocol yet</p>
            ) : (
              <div className="space-y-2">
                {protocolProducts.map((pp) => (
                  <div 
                    key={pp.id}
                    className="flex items-center justify-between p-3 bg-stone-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {pp.product?.image_url && (
                        <img 
                          src={pp.product.image_url} 
                          alt="" 
                          className="w-10 h-10 rounded object-cover"
                        />
                      )}
                      <div>
                        <p className="font-medium text-[#2C2C2C]">{pp.product?.product_name || 'Unknown'}</p>
                        <p className="text-xs text-stone-500">{pp.product?.brand}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getRoleBadge(pp.role)}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveProduct(pp.id)}
                        className="h-8 w-8 text-stone-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProtocols;
